import React, { useEffect, useState } from 'react';
import { KatModal, KatSpinner } from '@amzn/katal-react';
import { useScript, SCRIPT_STATUS } from 'src/hooks/useScript';
import { logger } from 'src/logger';
import { DEPLOYED_STAGE } from 'src/constants/UrlConstants';
import {
  CAPTCHA_INTERGRATION_URL,
  CAPTCHA_API_KEY,
  CAPTCHA_SUPPORTED_LOCALE,
  CAPTCHA_CONTAINER_ID,
  CAPTCHA_DEFAULT_LOCALE,
} from 'src/constants/WAFCaptchaConstants';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { emitClickMetrics, MetricsName } from 'src/metrics';

declare const AwsWafCaptcha: any;

interface WAFCaptchaContainerProps {
  captchaTitle: string;
  isCaptchaVisible: boolean;
  setCaptchaVisibility: (isCaptchaVisible: boolean) => void;
  onCaptchaValidationSuccess: () => void;
  onCaptchaValidationFailure: (error) => void;
  onCaptchaModalClose: () => void;
}

const WAFCaptchaContainer: React.FC<WAFCaptchaContainerProps> = ({
  captchaTitle,
  isCaptchaVisible,
  setCaptchaVisibility,
  onCaptchaValidationSuccess,
  onCaptchaValidationFailure,
  onCaptchaModalClose,
}) => {
  const stage = DEPLOYED_STAGE.toUpperCase();
  const scriptStatus = useScript(CAPTCHA_INTERGRATION_URL[stage]);
  const { localizationContext } = useLocalizationContext();
  const [isLoading, setLoader] = useState(true);

  useEffect(() => {
    if (scriptStatus === SCRIPT_STATUS.READY && isCaptchaVisible) {
      logger.info('Loading captcha for verification');
      const container = document.querySelector(`#${CAPTCHA_CONTAINER_ID}`);
      AwsWafCaptcha.renderCaptcha(container, {
        apiKey: CAPTCHA_API_KEY[stage],
        skipTitle: true,
        defaultLocale: getLocale(),
        dynamicWidth: true,
        onLoad: () => {
          setLoader(false);
        },
        onSuccess: async () => {
          setCaptchaVisibility(false);
          onCaptchaValidationSuccess();
          emitClickMetrics(
            MetricsName.WAF_CAPTCHA,
            'WAFCaptcha.CaptchaValidationSuccess',
          );
        },
        onError: (error: any) => {
          onCaptchaValidationFailure(error);
          emitClickMetrics(
            MetricsName.WAF_CAPTCHA,
            'WAFCaptcha.CaptchaValidationFailure',
          );
        },
      });
    }
  }, [scriptStatus, isCaptchaVisible]);

  function getLocale(): string {
    if (localizationContext.getLocale() === 'ar-AE') {
      return 'ar-SA';
    }
    return CAPTCHA_SUPPORTED_LOCALE.includes(localizationContext.getLocale())
      ? localizationContext.getLocale()
      : CAPTCHA_DEFAULT_LOCALE;
  }

  return (
    <KatModal
      visible={isCaptchaVisible}
      title={captchaTitle}
      onClose={() => {
        setCaptchaVisibility(false);
        onCaptchaModalClose();
      }}
    >
      {isLoading && <KatSpinner data-testid="captchaLoader" />}
      <div id={CAPTCHA_CONTAINER_ID} />
    </KatModal>
  );
};

export default WAFCaptchaContainer;
